import React, { useEffect, useState } from "react";
import moment from "moment";
import { Plus, Trash2, Zap, Pencil } from "lucide-react";
import { Modal } from "react-bootstrap";
import { Pagination, Stack } from "@mui/material";
import { Loader } from "../loader";
import api from "../../../api";
import "../AddPost.css";
import "./Live.css";
import "../AdminMarket.css";

const INITIAL_FORM = {
  position: "",
  coin: "",
  leverage: "",
  entryPrice: "",
  targetPrice: "",
  stopLoss: "",
  rr: "",
  rr_status: "",
  closeReason: "",
  closedPrice: "",
  pnl: "",
  pnlPercent: "",
  status: "OPEN",
  description: "",
};

const positionBadge = (pos) => {
  if (pos === "LONG") return <span className="badge" style={{ background: "#d1fae5", color: "#065f46", fontSize: 12 }}>▲ LONG</span>;
  if (pos === "SHORT") return <span className="badge" style={{ background: "#fee2e2", color: "#991b1b", fontSize: 12 }}>▼ SHORT</span>;
  return <span className="text-muted">-</span>;
};

const statusBadge = (s) => {
  if (s === "OPEN") return <span className="badge bg-success" style={{ fontSize: 12 }}>OPEN</span>;
  if (s === "CLOSED") return <span className="badge bg-secondary" style={{ fontSize: 12 }}>CLOSED</span>;
  return <span className="badge bg-light text-dark" style={{ fontSize: 12 }}>{s || "-"}</span>;
};

function FutureTrade() {
  const [trades, setTrades] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loader, setLoader] = useState(true);

  const [show, setShow] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [error, setError] = useState("");

  const [showEdit, setShowEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(INITIAL_FORM);
  const [editLoader, setEditLoader] = useState(false);
  const [editError, setEditError] = useState("");

  const fetchTrades = async (p = 1) => {
    try {
      setLoader(true);
      const res = await api.futureTradeGetAdmin({ page: p });
      if (res?.success) {
        setTrades(res.trades);
        setTotalPages(Math.ceil(res.count / res.limit) || 1);
      }
    } catch (e) {
      console.error(e.message);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => { fetchTrades(page); }, [page]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleEditChange = (e) => setEditForm({ ...editForm, [e.target.name]: e.target.value });

  const validate = (f) => {
    if (!f.coin) { return "Coin is required."; }
    if (!f.position) { return "Position (LONG/SHORT) is required."; }
    if (!f.entryPrice) { return "Entry price is required."; }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate(form);
    if (err) { setError(err); return; }
    setSubmitLoader(true);
    setError("");
    try {
      const res = await api.futureTradeAdd(form);
      if (res?.success) {
        setShow(false);
        setForm(INITIAL_FORM);
        fetchTrades(1);
        setPage(1);
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Something went wrong");
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this trade?")) return;
    try {
      await api.futureTradeDelete({ _id: id });
      fetchTrades(page);
    } catch (e) {
      console.error(e.message);
    }
  };

  const handleEditOpen = (t) => {
    setEditId(t._id);
    setEditForm({
      position: t.position || "",
      coin: t.coin || "",
      leverage: t.leverage || "",
      entryPrice: t.entryPrice ?? "",
      targetPrice: t.targetPrice ?? "",
      stopLoss: t.stopLoss ?? "",
      rr: t.rr ?? "",
      rr_status: t.rr_status || "",
      closeReason: t.closeReason || "",
      closedPrice: t.closedPrice ?? "",
      pnl: t.pnl ?? "",
      pnlPercent: t.pnlPercent ?? "",
      status: t.status || "OPEN",
      description: t.description || "",
    });
    setEditError("");
    setShowEdit(true);
  };

  const handleEditSubmit = async () => {
    const err = validate(editForm);
    if (err) { setEditError(err); return; }
    setEditLoader(true);
    setEditError("");
    try {
      const res = await api.futureTradeEdit({ _id: editId, ...editForm });
      if (res?.success) {
        setShowEdit(false);
        fetchTrades(page);
      }
    } catch (e) {
      setEditError(e?.response?.data?.message || "Something went wrong");
    } finally {
      setEditLoader(false);
    }
  };

  const TradeFields = ({ values, onChange }) => (
    <div className="trade-grid">
      <div className="trade-field">
        <label className="trade-label">Coin <span className="required-star">*</span></label>
        <input type="text" name="coin" className="trade-input" placeholder="e.g. BTC, ETH" value={values.coin} onChange={onChange} />
      </div>
      <div className="trade-field">
        <label className="trade-label">Position <span className="required-star">*</span></label>
        <select name="position" className="trade-input trade-select" value={values.position} onChange={onChange}>
          <option value="">Select</option>
          <option value="LONG">LONG</option>
          <option value="SHORT">SHORT</option>
        </select>
      </div>
      <div className="trade-field">
        <label className="trade-label">Entry Price <span className="required-star">*</span></label>
        <input type="number" name="entryPrice" className="trade-input" placeholder="e.g. 67500" value={values.entryPrice} onChange={onChange} />
      </div>
      <div className="trade-field">
        <label className="trade-label">Target Price</label>
        <input type="number" name="targetPrice" className="trade-input" placeholder="e.g. 72000" value={values.targetPrice} onChange={onChange} />
      </div>
      <div className="trade-field">
        <label className="trade-label">Stop Loss</label>
        <input type="number" name="stopLoss" className="trade-input" placeholder="e.g. 65000" value={values.stopLoss} onChange={onChange} />
      </div>
      <div className="trade-field">
        <label className="trade-label">Leverage</label>
        <div className="trade-input-suffix">
          <input type="text" name="leverage" className="trade-input" placeholder="e.g. 10" value={values.leverage} onChange={onChange} />
          <span className="suffix-label">x</span>
        </div>
      </div>
      <div className="trade-field">
        <label className="trade-label">R:R Ratio</label>
        <input type="number" name="rr" className="trade-input" placeholder="e.g. 3.5" value={values.rr} onChange={onChange} />
      </div>
      <div className="trade-field">
        <label className="trade-label">R:R Status</label>
        <select name="rr_status" className="trade-input trade-select" value={values.rr_status} onChange={onChange}>
          <option value="">Select</option>
          <option value="APPROVE">APPROVE</option>
          <option value="NEUTRAL">NEUTRAL</option>
          <option value="REJECT">REJECT</option>
        </select>
      </div>
      <div className="trade-field">
        <label className="trade-label">Status</label>
        <select name="status" className="trade-input trade-select" value={values.status} onChange={onChange}>
          <option value="OPEN">OPEN</option>
          <option value="CLOSED">CLOSED</option>
        </select>
      </div>
      <div className="trade-field">
        <label className="trade-label">Closed Price</label>
        <input type="number" name="closedPrice" className="trade-input" placeholder="e.g. 71800" value={values.closedPrice} onChange={onChange} />
      </div>
      <div className="trade-field">
        <label className="trade-label">PNL (USD)</label>
        <input type="number" name="pnl" className="trade-input" placeholder="e.g. 320" value={values.pnl} onChange={onChange} />
      </div>
      <div className="trade-field">
        <label className="trade-label">PNL (%)</label>
        <div className="trade-input-suffix">
          <input type="number" name="pnlPercent" className="trade-input" placeholder="e.g. 6.4" value={values.pnlPercent} onChange={onChange} />
          <span className="suffix-label">%</span>
        </div>
      </div>
      <div className="trade-field" style={{ gridColumn: "1 / -1" }}>
        <label className="trade-label">Close Reason</label>
        <input type="text" name="closeReason" className="trade-input" placeholder="Optional close reason" value={values.closeReason} onChange={onChange} />
      </div>
      <div className="trade-field" style={{ gridColumn: "1 / -1" }}>
        <label className="trade-label">Description / Note</label>
        <textarea name="description" className="trade-input" rows={3} placeholder="Optional note or analysis" value={values.description} onChange={onChange} style={{ resize: "vertical" }} />
      </div>
    </div>
  );

  return (
    <div className="p-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-3">
          <div className="rounded-3 d-flex align-items-center justify-content-center"
            style={{ width: 56, height: 56, background: "linear-gradient(135deg,#0ea5e9,#0284c7)" }}>
            <Zap size={26} color="#fff" />
          </div>
          <div>
            <h4 className="mb-0 fw-700">Futures Trades</h4>
            <span className="badge" style={{ background: "#0ea5e9", fontSize: 12 }}>
              {trades.length} trade{trades.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
        <button className="btn text-white fw-600 d-flex align-items-center gap-2"
          style={{ background: "#0ea5e9", borderRadius: 10 }} onClick={() => setShow(true)}>
          <Plus size={18} /> Add Trade
        </button>
      </div>

      <div className="card border-0 shadow-sm rounded-3">
        <div className="card-body p-0">
          {loader ? (
            <div className="py-5"><Loader /></div>
          ) : trades.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <Zap size={40} className="mb-3 opacity-25" />
              <p className="mb-0">No trades yet. Click <strong>Add Trade</strong> to get started.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead style={{ background: "#f0f9ff" }}>
                  <tr>
                    <th className="ps-4 py-3 text-muted fw-600" style={{ fontSize: 13 }}>#</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>COIN</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>POSITION</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>ENTRY</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>TARGET</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>SL</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>LEVERAGE</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>STATUS</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>CREATED</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map((t, i) => (
                    <tr key={t._id}>
                      <td className="ps-4 text-muted" style={{ fontSize: 13 }}>{(page - 1) * 10 + i + 1}</td>
                      <td className="fw-600">{t.coin || "-"}</td>
                      <td>{positionBadge(t.position)}</td>
                      <td>{t.entryPrice ?? "-"}</td>
                      <td>{t.targetPrice ?? "-"}</td>
                      <td>{t.stopLoss ?? "-"}</td>
                      <td>{t.leverage ? `${t.leverage}x` : "-"}</td>
                      <td>{statusBadge(t.status)}</td>
                      <td className="text-muted" style={{ fontSize: 13 }}>
                        {t.createdAt ? moment(t.createdAt).format("MMM D, YYYY h:mm A") : "-"}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => handleEditOpen(t)}>
                            <Pencil size={14} />
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(t._id)}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Stack spacing={2}>
            <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" />
          </Stack>
        </div>
      )}

      {/* Add Modal */}
      <Modal show={show} onHide={() => { setShow(false); setForm(INITIAL_FORM); setError(""); }} size="xl" centered className="modern-modal">
        <Modal.Body className="modern-modal-body">
          <div className="modal-header-section">
            <h3 className="modal-title">Add Future Trade</h3>
            <button className="modal-close-btn" onClick={() => { setShow(false); setForm(INITIAL_FORM); setError(""); }}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="modal-content-section">
            {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}
            <TradeFields values={form} onChange={handleChange} />
            <p className="trade-required-note mt-2"><span className="required-star">*</span> Required fields</p>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button className="btn btn-light fw-600 px-4" onClick={() => { setShow(false); setForm(INITIAL_FORM); setError(""); }}>Cancel</button>
              <button className="btn text-white fw-600 px-4"
                style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)", border: "none" }}
                onClick={handleSubmit} disabled={submitLoader}>
                {submitLoader ? "Saving..." : "Save Trade"}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)} size="xl" centered className="modern-modal">
        <Modal.Body className="modern-modal-body">
          <div className="modal-header-section">
            <h3 className="modal-title">Edit Future Trade</h3>
            <button className="modal-close-btn" onClick={() => setShowEdit(false)}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="modal-content-section">
            {editError && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{editError}</div>}
            <TradeFields values={editForm} onChange={handleEditChange} />
            <p className="trade-required-note mt-2"><span className="required-star">*</span> Required fields</p>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button className="btn btn-light fw-600 px-4" onClick={() => setShowEdit(false)}>Cancel</button>
              <button className="btn text-white fw-600 px-4"
                style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)", border: "none" }}
                onClick={handleEditSubmit} disabled={editLoader}>
                {editLoader ? "Saving..." : "Update Trade"}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default FutureTrade;
